import { useEffect, useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material'
import { useParams, useLocation } from 'react-router-dom'
import * as RT from '../config/routeType'
import { useCityBoundaries } from './useCityBoundaries'
import { useBoundaryVisibility } from './useBoundaryVisibility'

// Importar controles
import FilterControl from '../components/MapboxGL/controls/FilterControl'
import LocationCaptureControl from '../components/MapboxGL/controls/LocationCaptureControl'
import BoundaryControl from '../components/MapboxGL/controls/BoundaryControl'
import SatelliteControl from '../components/MapboxGL/controls/SatelliteControl'
import PointsOnlyControl from '../components/MapboxGL/controls/PointsOnlyControl'
import TreeCodeLabelsControl from '../components/MapboxGL/controls/TreeCodeLabelsControl'
import ExpandMapControl from '../components/MapboxGL/controls/ExpandMapControl'
import LegendToggleControl from '../components/MapboxGL/controls/LegendToggleControl'

/**
 * Hook para gestionar controles personalizados del mapa
 * Retorna el anchorEl para el popover de filtros
 *
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Indica si el mapa está cargado
 * @param {Object} store - Store de Redux
 */
export const useMapControls = (
  map,
  mapLoaded,
  store,
  { minimalControls = false, alertRef, legendItems = [], onLegendToggle = null } = {}
) => {
  const theme = useTheme()
  const location = useLocation()
  const { treeId } = useParams()
  const { cityName } = useCityBoundaries()

  const { setBoundaryVisibility } = useBoundaryVisibility(map, mapLoaded)

  const [filterAnchorEl, setFilterAnchorEl] = useState(null)

  const addMode = useSelector(state => state.mapState.addMode)
  const coords = useSelector(state => state.mapState.coords)

  const controlsRef = useRef({
    cityLabel: null,
    filter: null,
    boundary: null,
    locationCapture: null,
    satellite: null,
    pointsOnly: null,
    treeCodeLabels: null,
    expandMap: null,
    mapInfo: null,
    legendToggle: null,
  })

  const controlsInitialized = useRef(false)
  const themeRef = useRef(theme)
  const onLegendToggleRef = useRef(onLegendToggle)
  const setBoundaryVisibilityRef = useRef(setBoundaryVisibility)
  themeRef.current = theme
  onLegendToggleRef.current = onLegendToggle
  setBoundaryVisibilityRef.current = setBoundaryVisibility

  const shouldShowCaptureGPS = useCallback(() => {
    const isManagementPage = location.pathname.includes(RT.MANAGEMENT_TREE)
    const isRegisterTreePage = location.pathname.includes(RT.REGISTER_TREE)
    const hasTreeSelected = Boolean(treeId)

    // Mostrar botón GPS si:
    // - Estás en página de registro (siempre)
    // - O estás en página de edición con un árbol seleccionado
    return isManagementPage ? hasTreeSelected : isRegisterTreePage
  }, [location.pathname, treeId])

  const handleFilterToggle = useCallback(anchorEl => {
    setFilterAnchorEl(prevAnchor => (prevAnchor ? null : anchorEl))
  }, [])

  // Identidad estable: setBoundaryVisibility cambia con el cantón. Si este callback
  // cambiara, el efecto principal se re-ejecutaría y su cleanup quitaría todos los
  // controles, incluidos los creados en efectos aparte (Solo puntos, Leyendas).
  const handleBoundaryToggle = useCallback(visible => {
    setBoundaryVisibilityRef.current(visible)
  }, [])

  useEffect(() => {
    if (!map || !mapLoaded || controlsInitialized.current) return

    controlsInitialized.current = true
    const controlsSnapshot = controlsRef.current

    // Control de filtros (oculto en modo minimal)
    if (!minimalControls) {
      const filterControl = new FilterControl(store, themeRef.current, handleFilterToggle, alertRef)
      map.addControl(filterControl, 'top-left')
      controlsSnapshot.filter = filterControl
    }

    // Control de límites de ciudad (oculto en modo minimal)
    if (!minimalControls) {
      const boundaryControl = new BoundaryControl(
        store,
        themeRef.current,
        handleBoundaryToggle,
        false
      )
      map.addControl(boundaryControl, 'top-left')
      controlsSnapshot.boundary = boundaryControl
    }

    // ✅ Control satelital (NUEVO - siempre visible)
    const satelliteControl = new SatelliteControl(store, themeRef.current)
    map.addControl(satelliteControl, 'top-right')
    controlsSnapshot.satellite = satelliteControl

    // Códigos de árbol (siempre visible, debajo del satelital)
    const treeCodeLabelsControl = new TreeCodeLabelsControl(store, themeRef.current)
    map.addControl(treeCodeLabelsControl, 'top-right')
    controlsSnapshot.treeCodeLabels = treeCodeLabelsControl

    // Control de expandir mapa (solo en modo minimal / registro)
    if (minimalControls) {
      const expandControl = new ExpandMapControl(store, themeRef.current)
      map.addControl(expandControl, 'top-right')
      controlsSnapshot.expandMap = expandControl
    }

    // Cleanup al desmontar
    return () => {
      Object.entries(controlsSnapshot).forEach(([key, control]) => {
        if (control) {
          try {
            map.removeControl(control)
          } catch (e) {
            console.warn(`Error removiendo control ${key}:`, e)
          }
          controlsSnapshot[key] = null
        }
      })
      controlsInitialized.current = false
    }
  }, [
    map,
    mapLoaded,
    minimalControls,
    store,
    handleBoundaryToggle,
    handleFilterToggle,
    alertRef,
  ])

  // Manejo dinámico del control de captura GPS
  useEffect(() => {
    if (!map || !mapLoaded || !controlsInitialized.current) return

    const locationControl = controlsRef.current.locationCapture
    const needsCapture = shouldShowCaptureGPS()

    if (needsCapture && !locationControl) {
      const newLocationControl = new LocationCaptureControl(
        store,
        themeRef.current,
        shouldShowCaptureGPS
      )
      map.addControl(newLocationControl, 'top-left')
      controlsRef.current.locationCapture = newLocationControl
    } else if (!needsCapture && locationControl) {
      try {
        map.removeControl(locationControl)
      } catch (e) {
        console.warn('Error removiendo locationControl:', e)
      }
      controlsRef.current.locationCapture = null
    } else if (needsCapture && locationControl) {
      locationControl._renderContent()
    }
  }, [treeId, location.pathname, map, mapLoaded, store, shouldShowCaptureGPS])

  // Selector de leyendas del mapa, junto a los controles nativos superiores.
  // Se maneja de forma dinámica para poder sumar/quitar leyendas por pantalla.
  useEffect(() => {
    if (!map || !mapLoaded || !controlsInitialized.current) return

    const existing = controlsRef.current.legendToggle
    const shouldShow = !minimalControls && legendItems.length > 0

    if (shouldShow && !existing) {
      const legendToggleControl = new LegendToggleControl(
        store,
        themeRef.current,
        legendItems,
        (legendId, visible) => onLegendToggleRef.current?.(legendId, visible)
      )
      map.addControl(legendToggleControl, 'top-right')
      controlsRef.current.legendToggle = legendToggleControl
      return
    }

    if (!shouldShow && existing) {
      try {
        map.removeControl(existing)
      } catch (e) {
        console.warn('Error removiendo legendToggleControl:', e)
      }
      controlsRef.current.legendToggle = null
      return
    }

    if (shouldShow && existing) {
      existing.setLegends?.(legendItems)
      existing.updateOnToggle?.((legendId, visible) => {
        onLegendToggleRef.current?.(legendId, visible)
      })
    }
  }, [map, mapLoaded, minimalControls, store, legendItems])

  // Control de vista plana / comparación de verificados, debajo del satelital.
  // Siempre visible para que cualquier usuario pueda alternar puntos/modelos 3D.
  // Manejo dinámico para cubrir recargas de estilo sin duplicar controles.
  useEffect(() => {
    if (!map || !mapLoaded || !controlsInitialized.current) return

    const existing = controlsRef.current.pointsOnly

    if (!existing) {
      const pointsOnlyControl = new PointsOnlyControl(store, themeRef.current)
      map.addControl(pointsOnlyControl, 'top-right')
      controlsRef.current.pointsOnly = pointsOnlyControl
    }
  }, [map, mapLoaded, store])

  // Actualizar nombre de ciudad
  useEffect(() => {
    const cityControl = controlsRef.current.cityLabel
    if (cityControl && cityName) {
      cityControl.updateCity(cityName)
    }
  }, [cityName])

  // Actualizar controles cuando cambia el estado
  useEffect(() => {
    Object.values(controlsRef.current).forEach(control => {
      if (control && typeof control.update === 'function') {
        control.update()
      }
    })
  }, [addMode, coords])

  // Sincroniza estado actual del botón nativo de límites
  const updateBoundaryControlState = useCallback(state => {
    const boundaryControl = controlsRef.current.boundary
    if (boundaryControl && typeof boundaryControl.setControlState === 'function') {
      boundaryControl.setControlState(state)
    }
  }, [])

  // Actualiza callback del toggle nativo (tenant/cantón según contexto)
  const updateBoundaryToggleCallback = useCallback(onToggle => {
    const boundaryControl = controlsRef.current.boundary
    if (boundaryControl && typeof boundaryControl.updateOnToggle === 'function') {
      boundaryControl.updateOnToggle(onToggle)
    }
  }, [])

  return {
    controls: controlsRef.current,
    filterAnchorEl,
    setFilterAnchorEl,
    // Exponer funciones para control de boundary
    updateBoundaryControlState,
    updateBoundaryToggleCallback,
    setBoundaryVisibility,
  }
}
